import { useState, useEffect } from 'react';
import { fetchWholesalersByState, submitRegistration } from '../api/registrationApi';
import { lookupAbn } from '../api/abnApi';

const initialState = {
  abn: '',
  businessName: '',
  contactName: '',
  street: '',
  city: '',
  state: '',
  postcode: '',
  wholesaler: '',
  wholesalerAccountNumber: '',
  phoneNumber: '',
  emailAddress: '',
  confirmEmailAddress: '',
  tobaccoLicenceNumber: '',
  expiryDate: '',
};

export default function useRegistrationForm() {
  const [form, setForm] = useState(initialState);
  const [photo, setPhoto] = useState(null);
  const [wholesalers, setWholesalers] = useState([]);
  const [status, setStatus] = useState({ loading: false, message: '', error: false });
  const [abnStatus, setAbnStatus] = useState({ loading: false, message: '', error: false });
  const [businessNameOptions, setBusinessNameOptions] = useState([]);

  useEffect(() => {
    if (!form.state) {
      setWholesalers([]);
      return;
    }
    fetchWholesalersByState(form.state)
      .then((list) => {
        const sorted = [...list].sort((a, b) => a.localeCompare(b));
        setWholesalers(sorted);
      })
      .catch(() => setWholesalers([]));
  }, [form.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'state' ? { wholesaler: '' } : {}),
    }));
  };

  const handleAddressAutofill = (addressData) => {
    setForm((prev) => ({
      ...prev,
      ...addressData,
      wholesaler: '',
    }));
  };

  const handleAbnLookup = async (abnValue) => {
    const clean = abnValue.replace(/\s/g, '');
    if (clean.length !== 11) return;

    setAbnStatus({ loading: true, message: '', error: false });
    setBusinessNameOptions([]);
    setForm((prev) => ({ ...prev, businessName: '' }));

    try {
      const data = await lookupAbn(clean);
      const names = data.businessNames || [];
      setBusinessNameOptions(names);

      if (names.length === 1) {
        setForm((prev) => ({ ...prev, businessName: names[0] }));
      }

      setAbnStatus({
        loading: false,
        message: data.warning || `Verified: ${data.entityType || 'Active ABN'}`,
        error: !!data.warning,
      });
    } catch (err) {
      setBusinessNameOptions([]);
      setAbnStatus({
        loading: false,
        message: err.response?.data?.message || 'ABN not found. Please check and try again.',
        error: true,
      });
    }
  };

  const handleFileChange = (e) => {
    setPhoto(e.target.files[0]);
  };

  const resetForm = () => {
    setForm(initialState);
    setPhoto(null);
    setWholesalers([]);
    setAbnStatus({ loading: false, message: '', error: false });
    setBusinessNameOptions([]);
  };

  const handleSubmit = async (e, captchaToken) => {
    e.preventDefault();

    if (!form.businessName) {
      setStatus({ loading: false, message: 'Please select your business name from the ABN lookup.', error: true });
      return false;
    }

    if (form.emailAddress.trim().toLowerCase() !== form.confirmEmailAddress.trim().toLowerCase()) {
      setStatus({ loading: false, message: 'Email addresses do not match. Please check and try again.', error: true });
      return false;
    }

    if (!photo) {
      setStatus({ loading: false, message: 'Please upload a photo of your tobacco licence.', error: true });
      return false;
    }

    if (!captchaToken) {
      setStatus({ loading: false, message: 'reCAPTCHA verification failed. Please try again.', error: true });
      return false;
    }

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === 'confirmEmailAddress') return;
      formData.append(key, value);
    });
    formData.append('tobaccoLicencePhoto', photo);
    formData.append('captchaToken', captchaToken);

    setStatus({ loading: true, message: '', error: false });

    try {
      const res = await submitRegistration(formData);
      setStatus({ loading: false, message: res.message, error: false });
      resetForm();
      return true;
    } catch (err) {
      setStatus({
        loading: false,
        message: err.response?.data?.message || 'Submission failed. Please try again.',
        error: true,
      });
      return false;
    }
  };

  return {
    form,
    photo,
    wholesalers,
    status,
    abnStatus,
    businessNameOptions,
    handleChange,
    handleFileChange,
    handleSubmit,
    handleAddressAutofill,
    handleAbnLookup,
  };
}
